/**
 * PostureTabs — sticky pill toggle: Posture · Operations · History.
 *
 * URL-synced via `?tab=posture|operations|history`. `posture` is the default
 * and elides from the URL — link to /dashboard and you land on Posture.
 *
 * Right-side actions (Export PDF, Run evaluation) are passed in as a slot so
 * the parent owns their handlers (kept the existing QuickActions wired up).
 */

import { useSearchParams } from 'react-router-dom'
import clsx from 'clsx'

export type DashboardTab = 'posture' | 'operations' | 'history'

const TABS: ReadonlyArray<{
  id: DashboardTab
  label: string
  badge?: string
}> = [
  { id: 'posture', label: 'Posture', badge: '3 pillars' },
  { id: 'operations', label: 'Operations' },
  { id: 'history', label: 'History' },
]

interface Props {
  /** Right-side actions slot (Export PDF, Run evaluation, tenant selector). */
  actions?: React.ReactNode
}

export function useDashboardTab(): [DashboardTab, (next: DashboardTab) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get('tab')
  const current: DashboardTab =
    raw === 'operations' ? 'operations' :
    raw === 'history'    ? 'history' :
                           'posture'

  const setTab = (next: DashboardTab) => {
    setParams(prev => {
      // Default tab elides from the URL; explicit choices persist. Preserves
      // any other search params the user has set (e.g. filters from prior phases).
      if (next === 'posture') prev.delete('tab')
      else prev.set('tab', next)
      return prev
    }, { replace: true })
  }

  return [current, setTab]
}

export default function PostureTabs({ actions }: Props) {
  const [current, setTab] = useDashboardTab()

  return (
    <div
      role="tablist"
      aria-label="Dashboard view"
      className="
        sticky top-[64px] z-20 -mx-1 mb-9
        flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between
        border-b border-dark-700 bg-dark-900/95 px-1 py-4 backdrop-blur
        dark:bg-dark-900/95
      "
    >
      <div
        className="
          inline-flex items-center gap-0 self-start rounded-full border border-dark-600
          bg-dark-800 p-1
        "
      >
        {TABS.map(tab => {
          const active = tab.id === current
          const badge = tab.badge

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={active}
              aria-controls={`panel-${tab.id}`}
              id={`tab-${tab.id}`}
              onClick={() => setTab(tab.id)}
              className={clsx(
                'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-all duration-200',
                active
                  ? 'bg-[#212938] text-white shadow-[0_4px_12px_rgba(33,41,56,0.18)]'
                  : 'text-slate-500 hover:text-slate-100 dark:hover:text-slate-100',
              )}
            >
              {tab.label}
              {badge && (
                <span
                  className={clsx(
                    'rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                    active
                      ? 'bg-primary-500 text-[#212938]'
                      : 'bg-dark-700 text-slate-300',
                  )}
                >
                  {badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
    </div>
  )
}
